// SN-19949: in a Digital Form's print dialog, a blank (a run of 3+ underscores) becomes a field staff type into.
// The field is a non-editable shell the blank's width, holding an editable text island, so typing never pushes the
// rest of the line along. The saved template keeps its underscores: fields are made each time content is set, and
// only in the print dialog (word-processor-field.component.tsx). The PDF prints a field back as text on a line
// (printBlankFields in pdf-generation-v3.utils.ts).
import { format, parseISO } from "date-fns";
import type { Editor } from "tinymce";

import {
  BLANK_DATE_ATTRIBUTE,
  BLANK_FIELD_ATTRIBUTE,
  BLANK_LENGTH_ATTRIBUTE,
  BLANK_PAD_ATTRIBUTE,
  BLANK_TEXT_ATTRIBUTE
} from "components/pdf-generation-v3/form-print.utils";

const BLANK_RUN = /_{3,}/g;
// Where the caret sits in an empty field: without a character in it, a browser cannot place the caret inside the
// island. Printing removes it.
export const FIELD_CARET_ANCHOR = "\u200B";

// Text too long for its field shrinks down to this share of the line's size, then the field takes no more (the
// prototype's decision, SN-19949 spec).
export const MIN_TEXT_SCALE = 0.7;
// Set on a field that is full; the editor's stylesheet shows "Full" on it.
export const BLANK_FULL_CLASS = "blank-full";

// The editor-only tab above a date field that opens the browser's date picker. Printing removes it.
export const BLANK_DATE_TAB_ATTRIBUTE = "data-blank-date-tab";

// A label that names a date, at the end of the text before a blank: "Date", "Date of Delivery", "returned by",
// "Dated:". A word with "date" inside it ("Update", "Candidate") is not one.
const DATE_LABEL =
  /\b(dated?|date of [a-z]+|returned by|delivered on)\s*[:#]?\s*$/i;

export const isDateLabel = (textBefore: string) =>
  DATE_LABEL.test(textBefore.trim());

/** A date the picker gives (yyyy-MM-dd), written the way the app prints dates. */
export const formatPickedDate = (picked: string) =>
  picked ? format(parseISO(picked), "MM/dd/yyyy") : "";

export type BlankRun = { start: number; length: number };
export type Measure = (range: Range) => { widthPx: number; fontPx: number };

export const findBlankRuns = (text: string): BlankRun[] =>
  [...text.matchAll(BLANK_RUN)].map((match) => ({
    start: match.index ?? 0,
    length: match[0].length
  }));

const isInField = (node: Node) =>
  Boolean(node.parentElement?.closest(`[${BLANK_FIELD_ATTRIBUTE}]`));

const dateTab = (doc: Document) => {
  const tab = doc.createElement("span");
  tab.setAttribute(BLANK_DATE_TAB_ATTRIBUTE, "");
  tab.setAttribute("contenteditable", "false");
  tab.setAttribute("title", "Choose a date");
  tab.textContent = "\u{1F4C5}";
  const picker = doc.createElement("input");
  picker.type = "date";
  picker.tabIndex = -1;
  tab.appendChild(picker);
  return tab;
};

// The text on the same line before a blank, which says what the blank is for.
const textBefore = (node: Text, start: number) => {
  const line = node.parentElement?.closest("p, td, th, li, div");
  if (!line) return node.data.slice(0, start);
  const range = node.ownerDocument.createRange();
  range.setStart(line, 0);
  range.setEnd(node, start);
  return range.toString();
};

const fieldFor = (
  doc: Document,
  run: BlankRun,
  width: string,
  isDate: boolean
) => {
  const field = doc.createElement("span");
  field.setAttribute(BLANK_FIELD_ATTRIBUTE, "");
  field.setAttribute(BLANK_LENGTH_ATTRIBUTE, String(run.length));
  field.setAttribute("contenteditable", "false");
  field.style.width = width;
  const text = doc.createElement("span");
  text.setAttribute(BLANK_TEXT_ATTRIBUTE, "");
  text.setAttribute("contenteditable", "true");
  text.textContent = FIELD_CARET_ANCHOR;
  field.appendChild(text);
  if (isDate) {
    field.setAttribute(BLANK_DATE_ATTRIBUTE, "");
    field.appendChild(dateTab(doc));
  }
  return field;
};

/** Each run of 3+ underscores under `root` made a field of the run's width. Returns how many were made. */
export const wrapBlankRuns = (root: HTMLElement, measure: Measure) => {
  const doc = root.ownerDocument;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (!isInField(node) && findBlankRuns(node.textContent ?? "").length) {
      nodes.push(node as Text);
    }
  }
  let made = 0;
  nodes.forEach((node) => {
    // From the last run back, so the earlier runs' offsets still hold after each split.
    findBlankRuns(node.data)
      .reverse()
      .forEach((run) => {
        const range = doc.createRange();
        range.setStart(node, run.start);
        range.setEnd(node, run.start + run.length);
        const { widthPx, fontPx } = measure(range);
        const width =
          fontPx > 0
            ? `${Math.round((widthPx / fontPx) * 1000) / 1000}em`
            : `${Math.round(widthPx)}px`;
        const isDate = isDateLabel(textBefore(node, run.start));
        const blank = node.splitText(run.start);
        blank.splitText(run.length);
        blank.replaceWith(fieldFor(doc, run, width, isDate));
        made += 1;
      });
  });
  return made;
};

export const fitScale = (neededPx: number, availablePx: number) => {
  const scale = neededPx > availablePx ? availablePx / neededPx : 1;
  return scale < MIN_TEXT_SCALE
    ? { scale: MIN_TEXT_SCALE, fits: false }
    : { scale, fits: true };
};

/** The underscores that print after the typed text, out to the field's width. */
export const padCount = (length: number, typedPx: number, fieldPx: number) =>
  fieldPx > 0 ? Math.max(0, Math.round(length * (1 - typedPx / fieldPx))) : 0;

/** Whether a key pressed in a field must be stopped, so the caret and the edit never leave the field. */
export const fieldKeyAction = (
  key: string,
  offset: number,
  length: number,
  startsWithAnchor = false
) => {
  if (key === "Enter") return "stop";
  if (key === "Backspace" && offset <= (startsWithAnchor ? 1 : 0))
    return "stop";
  if (key === "Delete" && offset >= length) return "stop";
  return "allow";
};

const textIn = (field: HTMLElement) =>
  field.querySelector(`[${BLANK_TEXT_ATTRIBUTE}]`) as HTMLElement;

/** Applies a field's measurements: the shrunk size and the pad, or "Full" (and nothing else) when it can't fit. */
export const fitField = (
  field: HTMLElement,
  {
    neededPx,
    availablePx,
    basePx
  }: { neededPx: number; availablePx: number; basePx: number }
) => {
  const { scale, fits } = fitScale(neededPx, availablePx);
  if (!fits) {
    field.classList.add(BLANK_FULL_CLASS);
    return false;
  }
  field.classList.remove(BLANK_FULL_CLASS);
  textIn(field).style.fontSize =
    scale < 1 ? `${Math.round(basePx * scale * 10) / 10}px` : "";
  const length = Number(field.getAttribute(BLANK_LENGTH_ATTRIBUTE)) || 0;
  field.setAttribute(
    BLANK_PAD_ATTRIBUTE,
    String(padCount(length, neededPx * scale, availablePx))
  );
  return true;
};

const islandAtCaret = (editor: Editor) =>
  editor.dom.getParent(
    editor.selection.getStart(),
    `[${BLANK_TEXT_ATTRIBUTE}]`
  ) as HTMLElement | null;

const caretOffsetIn = (editor: Editor, island: HTMLElement) => {
  const before = editor.getDoc().createRange();
  const caret = editor.selection.getRng();
  before.setStart(island, 0);
  before.setEnd(caret.startContainer, caret.startOffset);
  return before.toString().length;
};

// Measured at the line's own size, so the scale is the share of it the text can keep.
const measureField = (editor: Editor, field: HTMLElement) => {
  const island = textIn(field);
  island.style.fontSize = "";
  const contents = editor.getDoc().createRange();
  contents.selectNodeContents(island);
  return {
    neededPx: contents.getBoundingClientRect().width,
    availablePx: field.getBoundingClientRect().width,
    basePx: parseFloat(editor.getWin().getComputedStyle(field).fontSize)
  };
};

const measureInEditor =
  (editor: Editor): Measure =>
  (range) => ({
    widthPx: range.getBoundingClientRect().width,
    fontPx: parseFloat(
      editor
        .getWin()
        .getComputedStyle(range.startContainer.parentElement as Element)
        .fontSize
    )
  });

// The last text each field held that fit, put back when an edit would not fit even at 70%.
const lastFit = new WeakMap<HTMLElement, string>();

/** Fits the field's text; when it cannot fit even at 70%, puts back the last text that did and marks it Full. */
const fitOrRevert = (editor: Editor, field: HTMLElement) => {
  const island = textIn(field);
  if (fitField(field, measureField(editor, field))) {
    lastFit.set(field, island.textContent ?? "");
    return true;
  }
  island.textContent = lastFit.get(field) ?? FIELD_CARET_ANCHOR;
  fitField(field, measureField(editor, field));
  field.classList.add(BLANK_FULL_CLASS);
  return false;
};

// ENG-2447: what the print dialog's "Fill in the blanks" panel reads and writes.

export type BlankField = {
  field: HTMLElement;
  /** The converted form's label for the blank, or the text printed before it on its line. */
  label: string;
  context: string;
  text: string;
  isEmpty: boolean;
};

// How much of the text before a blank names it when the converted form gave it no label.
const LABEL_FALLBACK_CHARS = 40;

const textBeforeField = (field: HTMLElement) => {
  const line = field.closest("p, td, th, li, div");
  if (!line) return "";
  const range = field.ownerDocument.createRange();
  range.setStart(line, 0);
  range.setEndBefore(field);
  return range.toString();
};

const fallbackLabel = (field: HTMLElement) =>
  textBeforeField(field)
    .replace(/[\u200B_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/[:#]$/, "")
    .trim()
    .slice(-LABEL_FALLBACK_CHARS)
    .trim();

/** Every field under `root` in document order, with its name and what it holds. */
export const listBlankFields = (root: HTMLElement): BlankField[] =>
  Array.from(
    root.querySelectorAll<HTMLElement>(`[${BLANK_FIELD_ATTRIBUTE}]`)
  ).map((field) => {
    const span = field.closest<HTMLElement>("[data-blank-label]");
    const text = (textIn(field).textContent ?? "").replace(
      FIELD_CARET_ANCHOR,
      ""
    );
    return {
      field,
      label: span?.getAttribute("data-blank-label") || fallbackLabel(field),
      context: span?.getAttribute("data-blank-context") ?? "",
      text,
      isEmpty: text.trim() === ""
    };
  });

/**
 * Writes `text` into the field the way typing does: it shrinks to fit, or stays at the last text that fit.
 * The editor is told, as typing tells it: the print dialog prints the form's value, which the editor
 * updates only on its own change events, and the write is one undo step.
 */
export const setBlankFieldText = (
  editor: Editor,
  field: HTMLElement,
  text: string
) => {
  let fits = false;
  editor.undoManager.transact(() => {
    textIn(field).textContent = text || FIELD_CARET_ANCHOR;
    fits = fitOrRevert(editor, field);
  });
  editor.nodeChanged();
  editor.dispatch("change");
  return fits;
};

/** Makes a field of every blank each time content is set, while `isEnabled` says so (the print dialog only). */
export const addBlankFields = (
  editor: Editor,
  { isEnabled }: { isEnabled: () => boolean }
) => {
  editor.on("SetContent", () => {
    if (isEnabled()) wrapBlankRuns(editor.getBody(), measureInEditor(editor));
  });

  editor.on("keydown", (event) => {
    const island = islandAtCaret(editor);
    if (!island) return;
    const text = island.textContent ?? "";
    const action = fieldKeyAction(
      event.key,
      caretOffsetIn(editor, island),
      text.length,
      text.startsWith(FIELD_CARET_ANCHOR)
    );
    if (action === "stop") event.preventDefault();
  });

  editor.on("paste", (event) => {
    if (!islandAtCaret(editor)) return;
    event.preventDefault();
    const text = event.clipboardData?.getData("text/plain") ?? "";
    editor.getDoc().execCommand("insertText", false, text.replace(/\s+/g, " "));
  });

  editor.on("click", (event) => {
    const tab = (event.target as Element | null)?.closest(
      `[${BLANK_DATE_TAB_ATTRIBUTE}]`
    );
    const picker = tab?.querySelector("input") as HTMLInputElement | null;
    if (!picker) return;
    event.preventDefault();
    try {
      picker.showPicker();
    } catch {
      // No picker in this browser: typing the date still works.
      const island = textIn(tab?.parentElement as HTMLElement);
      if (island.firstChild)
        editor.selection.setCursorLocation(island.firstChild, 0);
    }
  });

  editor.on("init", () =>
    editor.getBody().addEventListener("change", (event) => {
      const picker = event.target as HTMLInputElement;
      const field = picker.closest?.(
        `[${BLANK_DATE_ATTRIBUTE}]`
      ) as HTMLElement;
      if (!field || picker.type !== "date") return;
      editor.undoManager.transact(() => {
        textIn(field).textContent =
          formatPickedDate(picker.value) || FIELD_CARET_ANCHOR;
        fitField(field, measureField(editor, field));
      });
      lastFit.set(field, textIn(field).textContent ?? "");
    })
  );

  editor.on("input", () => {
    const island = islandAtCaret(editor);
    const field = island?.closest(`[${BLANK_FIELD_ATTRIBUTE}]`) as HTMLElement;
    if (!island || !field) return;
    if (fitOrRevert(editor, field)) return;
    if (island.firstChild) {
      editor.selection.setCursorLocation(
        island.firstChild,
        island.textContent?.length ?? 0
      );
    }
  });
};
